import { Delete as DeleteIcon, Edit as EditIcon, History as HistoryIcon } from "@mui/icons-material";
import React from "react";
import { Shop } from "../../../types/shop";
import {
  DataTable,
  makeColumnsSearchable,
} from "../../../components/common/DataTable/DataTable";
import { getShopPreferenceColor, getShopPreferenceLabel } from "../../../utils/statusColors";

interface ShopsListProps {
  shops: Shop[];
  isLoading: boolean;
  onEditClick?: (shop: Shop) => void;
  onDeleteClick?: (shop: Shop) => void;
  canEdit?: boolean;
  canDelete?: boolean;
  canViewHistory?: boolean;
  onHistoryClick?: (shop: Shop) => void;
  totalCount?: number;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  isLoadingRows?: boolean;
  onLoadMore?: () => void;

  enableSearch?: boolean;
  onSearchChange?: (filters: Record<string, any>) => void;
}

const ShopsList: React.FC<ShopsListProps> = ({
  shops,
  isLoading,
  onEditClick,
  onDeleteClick,
  canEdit = false,
  canDelete = false,
  canViewHistory = false,
  onHistoryClick,
  totalCount,
  hasNextPage,
  isLoadingMore,
  isLoadingRows,
  onLoadMore,

  enableSearch = false,
  onSearchChange,
}) => {
  const columns = [
    { field: "name", headerName: "Name", flex: 1 },
    { field: "address", headerName: "Address", flex: 1 },
    { field: "email", headerName: "Email/Name", flex: 1 },
    { field: "phoneNumber", headerName: "Phone Number", flex: 1 },
    {
      field: "preference",
      headerName: "Preference",
      flex: 0.8,
      renderCell: (value: number) => (
        <span
          style={{
            color: getShopPreferenceColor(value),
            fontWeight: "bold",
          }}
        >
          {getShopPreferenceLabel(value)}
        </span>
      ),
    },
    { field: "description", headerName: "Description", flex: 1.5 },
    { field: "workingHours", headerName: "Working Hours", flex: 1 },
  ];

  const actions = [
    ...(canEdit
      ? [
          {
            icon: <EditIcon />,
            label: "Edit",
            onClick: onEditClick!,
            color: "info" as const,
          },
        ]
      : []),
    ...(canViewHistory && onHistoryClick
      ? [
          {
            icon: <HistoryIcon />,
            label: "History",
            onClick: onHistoryClick,
            color: "primary" as const,
          },
        ]
      : []),
    ...(canDelete && onDeleteClick
      ? [
          {
            icon: <DeleteIcon />,
            label: "Delete",
            onClick: onDeleteClick,
            color: "warning" as const,
          },
        ]
      : []),
  ];

  // Create searchable columns with preference as select dropdown
  const preferenceColumn = columns.find((col) => col.field === "preference");
  const otherSearchableColumns = makeColumnsSearchable(
    columns.filter((col) => col.field !== "preference"),
    ["name", "address", "email", "phoneNumber", "description", "workingHours"],
    {
      name: "string",
      address: "string",
      email: "string",
      phoneNumber: "string",
      description: "string",
      workingHours: "string",
    }
  );

  // Add preference column with select dropdown
  const searchableColumns = [
    ...otherSearchableColumns,
    {
      field: "preference",
      headerName: "Preference",
      searchable: true,
      searchType: "select" as const,
      selectOptions: [
        { value: 0, label: "Trusted" },
        { value: 1, label: "DNU" },
      ],
      ...preferenceColumn,
    },
  ];

  return (
    <DataTable<Shop>
      items={shops}
      columns={columns}
      actions={actions}
      isLoading={isLoading}
      title="Shops"
      totalCount={totalCount}
      hasNextPage={hasNextPage}
      isLoadingMore={isLoadingMore}
      isLoadingRows={isLoadingRows}
      onLoadMore={onLoadMore}
      // Search functionality
      enableSearch={enableSearch}
      searchableColumns={searchableColumns}
      onSearchChange={onSearchChange}
    />
  );
};

export default ShopsList;
